/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { jsx } from '@compiled/react';
import { useIntl } from 'react-intl';

import { DescriptionBody } from '../../../../../../../common/ui/description-body';
import { processExcerpt } from '../../../../../../../common/utils/search-result-utils';

import { convertVideoDuration } from './convert-video-duration';
import { BaseVideoSearchResult } from './shared/base-video-search-result';
import { type VideoResultRendererProps } from './shared/types';

/**
 * Formats the meeting transcript title with the date appended.
 * Returns "{title} - {formatted date}" using the user's locale.
 */
const formatTitleWithDate = (
	title: string,
	lastModifiedDate: string | null | undefined,
	formatDate: ReturnType<typeof useIntl>['formatDate'],
): string => {
	if (!lastModifiedDate) {
		return title;
	}

	const formattedDate = formatDate(new Date(lastModifiedDate), {
		month: 'short',
		day: 'numeric',
		year: 'numeric',
	});

	return `${title} - ${formattedDate}`;
};

/**
 * Renderer for MS Teams meeting transcript search results.
 * Does not show video-specific features like thumbnail, duration, or chapters.
 * Appends the lastModifiedDate to the title for easier differentiation of transcripts.
 */
export const MeetingTranscriptRenderer: (props: VideoResultRendererProps) => JSX.Element = (
	props: VideoResultRendererProps,
) => {
	const { data, formatMessage } = props;
	const { excerpt, title, lastModifiedDate, entity } = data;
	const { durationInSeconds } = entity || {};
	const { formatDate } = useIntl();

	const videoDuration = convertVideoDuration(durationInSeconds, { readable: true, formatMessage });

	const body = <DescriptionBody description={processExcerpt(excerpt, true)} />;
	const customTitle = formatTitleWithDate(title, lastModifiedDate, formatDate);

	return (
		<BaseVideoSearchResult
			{...props}
			body={body}
			customTitle={customTitle}
			additionalMetadataParts={[['video-duration', videoDuration]]}
		/>
	);
};
